import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, formatErr } from "../../../../lib/api";
import { toast } from "sonner";
import { SectionCard } from "./shared";
import { ledgerBlank } from "./constants";
import { ACCOUNTING_BASE } from "./accountingNav";
import LedgerForm from "./LedgerForm";

export default function LedgerEditPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [ledger, setLedger] = useState(null);
  const [accountGroups, setAccountGroups] = useState([]);
  const [form, setForm] = useState(ledgerBlank);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [ledgerRes, groupsRes] = await Promise.all([
          api.get(`/society/accounting/engine/ledgers/${id}`),
          api.get("/society/accounting/engine/account-groups").catch(() => ({ data: [] })),
        ]);
        const row = ledgerRes.data;
        if (row.isSystem) {
          toast.error("System ledgers cannot be edited");
          navigate(`${ACCOUNTING_BASE}/ledgers/${id}`, { replace: true });
          return;
        }
        setLedger(row);
        setAccountGroups(Array.isArray(groupsRes.data) ? groupsRes.data : []);
        setForm({
          name: row.name || "", ledgerGroup: row.ledgerGroup || "expense", accountGroupId: row.accountGroupId || "",
          code: row.code || "", openingBalance: row.openingBalance || 0, openingSide: row.openingSide || "debit",
          openingAsOfDate: row.openingAsOfDate || "",
          active: row.active !== false, notes: row.notes || "",
        });
      } catch (err) {
        toast.error(formatErr(err, "Could not load this ledger"));
        navigate(`${ACCOUNTING_BASE}/ledgers`, { replace: true });
      } finally {
        setLoading(false);
      }
    })();
  }, [id, navigate]);

  const save = async () => {
    if (!form.name.trim()) { toast.error("Ledger name is required"); return; }
    try {
      setBusy(true);
      await api.patch(`/society/accounting/engine/ledgers/${id}`, {
        name: form.name, ledgerGroup: form.ledgerGroup, accountGroupId: form.accountGroupId || null,
        code: form.code, active: form.active, notes: form.notes,
        openingBalance: Number(form.openingBalance) || 0,
        openingSide: form.ledgerGroup === "cash" ? "debit" : (form.openingSide || "debit"),
        openingAsOfDate: form.openingAsOfDate || "",
      });
      toast.success("Ledger updated");
      navigate(`${ACCOUNTING_BASE}/ledgers/${id}`);
    } catch (err) {
      toast.error(formatErr(err, "Could not save ledger"));
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div className="text-sm text-[#6B7280]">Loading...</div>;
  if (!ledger) return null;

  return (
    <div className="space-y-5">
      <div>
        <Link to={`${ACCOUNTING_BASE}/ledgers/${id}`} className="text-xs font-semibold uppercase tracking-[0.16em] text-[#6B7280] hover:text-[#111827]">
          &larr; Back to ledger
        </Link>
        <h2 className="mt-1 text-lg font-extrabold text-[#111827]">Edit {ledger.name}</h2>
      </div>

      <SectionCard title="Ledger details">
        <LedgerForm
          form={form}
          setForm={setForm}
          accountGroups={accountGroups}
          groupLocked={!!ledger.hasPostedTransactions}
          busy={busy}
          onSubmit={save}
          onCancel={() => navigate(`${ACCOUNTING_BASE}/ledgers/${id}`)}
          submitLabel="Save changes"
          isEdit
        />
      </SectionCard>
    </div>
  );
}
